import { useQuery } from '@tanstack/react-query';
import type { QueryFunction, QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { BrokerNerdsBarkerCoreModelsInventoryEventBase, GetApiCatalogEventsParams } from './models';

export const getApiCatalogEvents = (params: GetApiCatalogEventsParams, options?: AxiosRequestConfig): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsInventoryEventBase[]>> => {
  return axios.get(`/api/Catalog/Events`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetApiCatalogEventsQueryKey = (params: GetApiCatalogEventsParams) => {
  return [`/api/Catalog/Events`, ...(params ? [params] : [])] as const;
};

export const getGetApiCatalogEventsQueryOptions = <TData = Awaited<ReturnType<typeof getApiCatalogEvents>>, TError = AxiosError<void>>(
  params: GetApiCatalogEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiCatalogEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiCatalogEventsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiCatalogEvents>>> = ({ signal }) => getApiCatalogEvents(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiCatalogEvents>>, TError, TData> & { queryKey: QueryKey };
};

export type GetApiCatalogEventsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiCatalogEvents>>>;
export type GetApiCatalogEventsQueryError = AxiosError<void>;

export const useGetApiCatalogEvents = <TData = Awaited<ReturnType<typeof getApiCatalogEvents>>, TError = AxiosError<void>>(
  params: GetApiCatalogEventsParams,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiCatalogEvents>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiCatalogEventsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};
